import React, { useEffect, useContext } from 'react';
import {
  Box,
  Stack,
  TextField,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import JurisdictionPicker from 'components/_pickers/JurisdictionPicker';
import ManualPicker from 'components/_pickers/ManualPicker';
import VerticalPicker from 'components/_pickers/VerticalPicker';
import { AppContext } from 'views/App/context';
import { neutral } from '_lex/_foundations/phxColors';
import { Manual } from 'views/App/Portal/Policy/_types/manuals/draft';

interface DTBPackageFormFieldsProps {
  orgId: string;
  packageName: string;
  jurisdictionId: string;
  manualId: string;
  verticalId: string;
  level: 'JURISDICTION' | 'GLOBAL' | 'AGENCY';
  isNew?: boolean;
  onPackageNameChange: (val: string) => void;
  onJurisdictionChange: (val: string) => void;
  onManualChange: (val: string) => void;
  onVerticalChange: (val: string) => void;
  onLevelChange: (val: 'JURISDICTION' | 'GLOBAL' | 'AGENCY') => void;
  disabled?: boolean;
}

const DTBPackageFormFields: React.FC<DTBPackageFormFieldsProps> = ({
  orgId,
  packageName,
  jurisdictionId,
  manualId,
  verticalId,
  level,
  isNew,
  onPackageNameChange,
  onJurisdictionChange,
  onManualChange,
  onVerticalChange,
  onLevelChange,
  disabled = false,
}) => {
  const { isLexipolStaff } = useContext(AppContext);

  // Automatically set level based on jurisdictionId only if not Lexipol staff
  useEffect(() => {
    if (!isLexipolStaff) {
      onLevelChange('AGENCY');
    }
  }, [jurisdictionId, onLevelChange, isLexipolStaff]);

  return (
    <Box sx={{ maxWidth: 700 }}>
      <Stack spacing={4}>
        {isNew && (
          <Typography
            variant='h5'
            sx={{
              color: neutral[600],
            }}
          >
            {t('dtbs.packages.create.form.new')}
          </Typography>
        )}

        <TextField
          label={t('dtbs.packages.create.form.title')}
          value={packageName}
          onChange={(e) => onPackageNameChange(e.target.value)}
          fullWidth
        />

        {isLexipolStaff && (
          <>
            <FormControl sx={{ mt: 0, pt: 0 }}>
              <FormLabel>{t('dtbs.library.level.label')}</FormLabel>
              <RadioGroup
                row
                value={level}
                onChange={(e) => onLevelChange(e.target.value as 'JURISDICTION' | 'GLOBAL')}
              >
                <FormControlLabel
                  value='GLOBAL'
                  control={<Radio />}
                  label={t('dtbs.library.level.GLOBAL')}
                  disabled={disabled}
                />
                <FormControlLabel
                  value='JURISDICTION'
                  control={<Radio />}
                  label={t('dtbs.library.level.JURISDICTION')}
                  disabled={disabled}
                />
              </RadioGroup>
            </FormControl>
          </>
        )}

        {level === 'JURISDICTION' && (
          <JurisdictionPicker
            id='jurisdiction-picker'
            label={t('dtbs.packages.create.form.jurisdiction')}
            value={jurisdictionId ? [jurisdictionId] : []}
            onChange={(val: string[]) => onJurisdictionChange(val[0] || '')}
            placeholder={t('dtbs.packages.create.form.chooseJurisdiction')}
            disabled={disabled}
          />
        )}

        <Box>
          <ManualPicker
            id='manual-picker'
            label={t('dtbs.packages.create.form.manual')}
            organizationId={orgId}
            jurisdictionId={jurisdictionId}
            globalOnly={level === 'GLOBAL'}
            value={manualId ? [manualId] : []}
            onChange={(val: string[]) => onManualChange(val[0] || '')}
            placeholder={t('dtbs.packages.create.form.chooseManual')}
            disabled={disabled || (level === 'JURISDICTION' && !jurisdictionId) || (!isNew && level === 'AGENCY')}
          />
          <FormHelperText>{t('dtbs.packages.create.form.manualHelper')}</FormHelperText>
        </Box>

        {/* Commenting out Vertical picker for now
        <VerticalPicker
          id='vertical-picker'
          label={t('dtbs.packages.create.form.vertical')}
          value={verticalId ? [verticalId] : []}
          onChange={(val: string[]) => onVerticalChange(val[0] || '')}
          placeholder={t('dtbs.packages.create.form.chooseVertical')}
        />
        */}
      </Stack>
    </Box>
  );
};

export default DTBPackageFormFields;
